//@flow
import React from 'react';
import PropTypes from 'prop-types';
import {ButtonGroup, Grid, Hidden, Typography} from '@material-ui/core';
import {
  ArrowBack,
  Equalizer,
  Facebook,
  HelpTwoTone,
  InfoOutlined,
  PlayCircleFilledTwoTone,
  Receipt,
} from '@material-ui/icons';
import {makeStyles} from '@material-ui/styles';
import {Link, Route, Switch, useLocation} from 'react-router-dom';
import CustomButton from '../../../elements/CustomButton/CustomButton';

import FacebookIntegrationContainer from '../../configure/social-medias/containers/FacebookIntegrationContainer';
import AlertDueDateTier from '../../tiers/components/AlertDueDateTier/AlertDueDateTier';

import type {Store, User} from '../../../entities';
import {colors} from '../../../assets/styles/colors';
import ReportsContainer from '../../marketing/reports/containers/ReportsContainer';
import {TIENDADA_LINKS} from '../../../constants';
// import TaxInvoiceForm from '../../tax-invoice/components/TaxInvoiceForm';
import TaxInvoice from '../../tax-invoice/components/TaxInvoice';
import TikTokPixelIntegration from '../../configure/social-medias/components/TikTokPixelIntegration';
import {useIsFeatureInTier} from '../../../utils/component';

type ConfigureSubRoute = {
  show: boolean,
  name: string | React$Node,
  path: string,
  component?: React$Node,
  icon: React$Node,
  disabled?: boolean,
  tierRestricted?: boolean,
  extra?: React$Node,
};

type Props = {
  store: Store,
  user: User,
};

export const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    background: '#f8f9fb',
  },
  navBackground: {
    [theme.breakpoints.down('sm')]: {
      background: 'white !important',
    },
  },
  content: {
    maxWidth: 1300,
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(4),
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(2),
    },
  },
  dividerVertical: {
    margin: theme.spacing(0, 1),
    background: colors.grey300,
  },
  contentBottom: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    background: '#f8f9fb',
    padding: theme.spacing(0, 4),
    [theme.breakpoints.down('sm')]: {
      marginBottom: 140,
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 2),
    },
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'black',
    [theme.breakpoints.down('xs')]: {
      fontSize: '18px',
    },
  },
  ayudaButton: {
    backgroundColor: 'white',
    color: colors.purple3,
    padding: '4px 13px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    margin: '0 14px 0 0',
    [theme.breakpoints.down('xs')]: {
      backgroundColor: 'unset',
      color: colors.purple3,
      fontSize: '12px',
      margin: '0 16px 0 0',
      padding: 0,
      boxShadow: 'unset',
    },
  },
  tutorialsButton: {
    backgroundColor: 'white',
    color: colors.purple3,
    padding: '4px 13px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    [theme.breakpoints.down('xs')]: {
      backgroundColor: 'unset',
      color: colors.purple3,
      padding: 0,
      boxShadow: 'unset',
    },
  },
  subRouteHeader: {
    fontSize: '1.125rem',
    fontWeight: 600,
    padding: 12,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    backgroundColor: colors.grey300,
    color: colors.grey800,
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
  },
  extraText: {
    margin: theme.spacing(0, 1),
    fontSize: '0.875rem',
    color: colors.grey700,
    fontWeight: 500,
  },
  upsellMessage: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1),
      flexWrap: 'wrap',
    },
    color: colors.purple3,
    background: colors.lightPurple,
    borderRadius: '12px',
    fontSize: '0.875rem',
  },
  upgradeTierButton: {
    marginLeft: 'auto',
    backgroundColor: 'unset',
    color: colors.purple3,
    boxShadow: 'unset',
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(1),
    },
  },
  configureButton: {
    fontSize: '1.125rem',
    fontWeight: 600,
    padding: '20px 20px',
    marginBottom: theme.spacing(2),
    textAlign: 'left',
    justifyContent: 'flex-start',
    backgroundColor: 'white',
    color: colors.grey800,
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
    whiteSpace: 'pre-wrap',
  },
  disabledConfigureButton: {
    fontSize: '1.125rem',
    fontWeight: 600,
    padding: '20px 20px',
    marginBottom: theme.spacing(2),
    textAlign: 'left',
    justifyContent: 'flex-start',
    backgroundColor: 'white',
    color: colors.grey800,
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
    whiteSpace: 'pre-wrap',
  },
  groupButton: {
    width: '100%',
    justifySelf: 'flex-end',
    backgroundColor: 'white',
    color: colors.purple3,
    padding: '20px 20px',
    marginBottom: theme.spacing(2),
    border: 'none',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
  },
  subRouteIcon: {
    fontSize: '1.5rem !important',
    marginRight: 12,
  },

  subRouteNameWrap: {
    height: 56,
    position: 'sticky',
    top: 72,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    color: colors.grey700,
    backgroundColor: colors.grey200,
    padding: theme.spacing(2),
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  subRouteNameText: {
    fontWeight: 600,
    fontSize: '1.125rem',
  },
  upsellMessageBottom: {
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'center',
    maxWidth: 670,
    // justifyContent: 'space-between',
    margin: theme.spacing(2),
    padding: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      position: 'fixed',
      bottom: 64,
      left: 0,
    },
    color: colors.purple3,
    background: colors.lightPurple,
    // opacity: 0.8,
    borderRadius: '12px',
    fontSize: '0.875rem',
  },
  newLabel: {
    background: '#F5F0FF',
    marginLeft: 10,
    fontWeight: 600,
    '& span': {
      color: '#7B39FF',
    },
  },
}));

function getTutorialLinkByPathname(pathname: string) {
  if (pathname.includes('facebook-integration')) {
    return TIENDADA_LINKS.tutorials.FACEBOOK_PIXEL;
  }

  if (pathname.includes('google-analytics')) {
    return TIENDADA_LINKS.tutorials.GOOGLE;
  }

  return TIENDADA_LINKS.tutorials.GENERAL;
}

export default function Integrations(props: Props) {
  const classes = useStyles();
  const location = useLocation();

  const hasTaxInvoiceFeature = useIsFeatureInTier('TAX_INVOICE');

  const subRoutes: {[key: string]: ConfigureSubRoute} = {
    googleAnalytics: {
      show: true,
      name: 'Google Analytics/GTM',
      path: '/integrations/google-analytics',
      component: <ReportsContainer />,
      icon: <Equalizer className={classes.subRouteIcon} />,
    },

    facebookShop: {
      show: true,
      name: 'Facebook / Instagram',
      path: '/integrations/facebook-integration',
      component: <FacebookIntegrationContainer />,
      icon: <Facebook className={classes.subRouteIcon} />,
    },

    tiktokPixel: {
      show: true,
      name: 'TikTok Pixel',
      path: '/integrations/tiktok-pixel',
      component: <TikTokPixelIntegration />,
      icon: <Facebook className={classes.subRouteIcon} />,
    },

    taxInvoice: {
      show: hasTaxInvoiceFeature,
      name: 'Facturación Electrónica',
      path: '/integrations/tax-invoice',
      component: <TaxInvoice />,
      icon: <Receipt className={classes.subRouteIcon} />,
    },
  };
  const subRoutesArray = Object.keys(subRoutes).map(k => subRoutes[k]);

  const subRoute: ?ConfigureSubRoute = subRoutesArray.find(
    (route: ConfigureSubRoute) => location.pathname.startsWith(route.path)
  );

  return (
    <div className={classes.root}>
      <AlertDueDateTier />
      <Hidden mdUp>
        {subRoute ? (
          <div className={classes.subRouteNameWrap}>
            <CustomButton to="/integrations">
              <ArrowBack />
            </CustomButton>
            {subRoute.icon}
            <Typography component={'span'} className={classes.subRouteNameText}>
              {subRoute.name}
            </Typography>
            {subRoute.extra && subRoute.extra}
          </div>
        ) : (
          <div className={classes.subRouteNameWrap}>
            <CustomButton to="/tools">
              <ArrowBack />
            </CustomButton>
            <Typography component={'span'} className={classes.subRouteNameText}>
              Regresar
            </Typography>
          </div>
        )}
      </Hidden>

      <main className={classes.content}>
        <Hidden smDown>
          <div className={classes.headerWrapper}>
            <Grid container justify="space-between" alignItems="center">
              <Grid item>
                <Typography className={classes.title}>Integraciones</Typography>
              </Grid>
              <Grid item>
                <CustomButton
                  href={TIENDADA_LINKS.customerSupport.general}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.ayudaButton}
                  startIcon={<HelpTwoTone />}
                >
                  Ayuda
                </CustomButton>
                <CustomButton
                  href={getTutorialLinkByPathname(location.pathname)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.tutorialsButton}
                  startIcon={<PlayCircleFilledTwoTone />}
                >
                  Tutoriales
                </CustomButton>
              </Grid>
            </Grid>
            {subRoute && (
              <div className={classes.subRouteHeader}>
                <div style={{display: 'flex', alignItems: 'center'}}>
                  {subRoute.icon} {subRoute.name}
                </div>
              </div>
            )}
          </div>
        </Hidden>
        {subRoute && subRoute.tierRestricted && (
          <div className={classes.upsellMessage}>
            <div style={{display: 'flex', alignItems: 'center'}}>
              <InfoOutlined style={{marginRight: 16}} />
              Suscríbete a nuestros planes para acceder a funcionalidades PRO e
              impulsar tu negocio
            </div>
            <CustomButton
              className={classes.upgradeTierButton}
              to="/tiers-list"
            >
              Mejorar Plan
            </CustomButton>
          </div>
        )}
        <Switch>
          <Route exact path="/integrations">
            <>
              {subRoutesArray.map((route: ConfigureSubRoute, idx: number) =>
                !route.show ? null : (
                  <CustomButton
                    key={idx}
                    startIcon={route.icon}
                    disabled={route.disabled}
                    component={Link}
                    to={route.path}
                    className={`${
                      route.disabled
                        ? classes.disabledConfigureButton
                        : classes.configureButton
                    }`}
                  >
                    {route.name}
                  </CustomButton>
                )
              )}
            </>
          </Route>
          {subRoutesArray.map((route: ConfigureSubRoute, idx: number) => (
            <Route key={`my-store-route-${idx}`} path={route.path}>
              <div className={classes.subRouteContent}>{route.component}</div>
            </Route>
          ))}
        </Switch>
      </main>
      <div className={classes.contentBottom}>
        <Hidden mdUp>
          <ButtonGroup>
            <CustomButton
              href={getTutorialLinkByPathname(location.pathname)}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.groupButton}
              startIcon={<PlayCircleFilledTwoTone />}
            >
              Tutoriales
            </CustomButton>
            <CustomButton
              href={TIENDADA_LINKS.customerSupport.general}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.groupButton}
              startIcon={<HelpTwoTone />}
            >
              Ayuda
            </CustomButton>
          </ButtonGroup>
        </Hidden>
      </div>
    </div>
  );
}

Integrations.propTypes = {
  store: PropTypes.object,
  user: PropTypes.object,
};
